import { action, computed, observable, reaction, runInAction } from "mobx";
import { Filter } from "./Filter";
import { Item } from "./Item";
import { ConfigInterface } from "./Config";
import { defer } from "lib/defer";
import { RawPostsApi as Api } from "lib/Api";
import { call } from "lib/apiHelper";

export interface InitialProps {
  titleUuid: string;
  columns: Column[];
  idScrambled: boolean;
}

export type Column =
  | "id"
  | "applicationId"
  | "eventId"
  | "env"
  | "nsaId"
  | "deviceId2"
  | "operationMode"
  | "productModel"
  | "json"
  | "lcRecordedAt"
  | "ncRecordedAt"
  | "reportIdentifier"
  | "postedAt"
  | "serialNumber";

let store: Store | undefined;

export function getStore() {
  if (!store) {
    throw new Error("Store not initialized");
  }
  return store;
}

export function createStore(props: InitialProps) {
  store = new Store(props);
}

class Store {
  app: AppStore;
  list: ListStore;
  detail: DetailStore;
  config: ConfigStore;

  constructor(props: InitialProps) {
    this.app = new AppStore(this, props);
    this.list = new ListStore(this);
    this.detail = new DetailStore(this);
    this.config = new ConfigStore(this);
  }
}

class StoreBase {
  protected parent: Store;

  constructor(parent: Store) {
    this.parent = parent;
  }
}

class AppStore extends StoreBase {
  readonly titleUuid: string;
  readonly idScrambled: boolean;
  readonly columns: Column[];

  constructor(parent: Store, props: InitialProps) {
    super(parent);
    this.titleUuid = props.titleUuid;
    this.idScrambled = props.idScrambled;
    this.columns = props.columns;
  }
}

class ListStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor items: Item[] = [];
  @observable accessor perPage = 10;
  @observable accessor currentPage = 1;
  @observable accessor totalPages = 0;
  @observable accessor totalCount = 0;
  @observable accessor selectedId: number | undefined = undefined;
  @observable accessor selectedIndex: number | undefined = undefined;
  @observable accessor filter = new Filter();

  constructor(parent: Store) {
    super(parent);

    reaction(
      () => [this.currentPage, this.perPage],
      () => this.search(),
    );
    reaction(
      () => this.selectedId,
      (selectedId) => {
        const item = this.items.find((item) => item.id === selectedId);
        if (item) {
          this.parent.detail.item = item;
        } else {
          this.parent.detail.getRawPost(selectedId);
        }
      },
    );
  }

  @action clearFilter() {
    this.filter.clear();
    this.search(1);
  }

  @action selectPost(selectedId: number) {
    const selectedIndex = this.items.findIndex(
      (item) => item.id === selectedId,
    );
    this.selectedId = selectedId;
    this.selectedIndex = selectedIndex < 0 ? undefined : selectedIndex;
  }

  @action selectNextPost() {
    if (this.isLoading) {
      return;
    }
    const items = this.items;
    const selectedIndex = this.selectedIndex;
    if (items.length == 0 || selectedIndex == null) {
      return;
    }
    if (items.length - 1 < selectedIndex) {
      return;
    }

    const item = items[selectedIndex + 1];
    if (item) {
      this.selectedId = item.id;
      this.selectedIndex = selectedIndex + 1;
    }
  }

  @action selectPrevPost() {
    if (this.isLoading) {
      return;
    }
    const items = this.items;
    const selectedIndex = this.selectedIndex;
    if (items.length == 0 || selectedIndex == null) {
      return;
    }
    if (selectedIndex <= 0) {
      return;
    }
    const item = items[selectedIndex - 1];
    if (item) {
      this.selectedId = item.id;
      this.selectedIndex = selectedIndex - 1;
    }
  }

  @action async search(page?: number) {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.indexTitlesRawPost(this.parent.app.titleUuid, {
        page: page ?? this.currentPage,
        per: this.perPage,
        q: this.filter.buildQuery(this.parent.config),
      }),
    );
    if (resp.code != 200) {
      return;
    }
    runInAction(() => {
      this.items = resp.body.rawPosts.map((item) =>
        Item.buildFromJsonApi(item, this.parent.app.idScrambled),
      );
      this.currentPage = resp.body.meta.currentPage;
      this.totalPages = resp.body.meta.totalPages;
      this.totalCount = resp.body.meta.totalCount;
      this.filter.updateUrlParams();
    });
  }
}

class DetailStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor tab = "list";
  @observable accessor item: Item | undefined = undefined;

  @action async getRawPost(id?: number) {
    if (id == null) {
      this.item = undefined;
      return;
    }
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(Api.showRawPost(id));
    if (resp.code != 200) {
      return;
    }
    this.item = Item.buildFromJsonApi(resp.body, this.parent.app.idScrambled);
  }
}

class ConfigStore extends StoreBase implements ConfigInterface {
  @observable accessor isOpened = false;
  @observable accessor idScrambled = false;
  @observable accessor eventIdExactMatch = true;
  @observable accessor displayIdAsHex = true;
  @observable accessor hexDataKeys: string[] = [];

  @computed get sortedHexDataKeys() {
    return this.hexDataKeys.slice().sort();
  }

  @action removeHexDataKey(key: string) {
    const index = this.hexDataKeys.indexOf(key);
    if (index !== -1) {
      this.hexDataKeys.splice(index, 1);
    }
  }
}
